import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { analyticsService } from "../services/analyticsService";
import { intercomService } from "../services/intercomService";
import { trackingService } from "../services/trackingService";
import { publicVisitService } from "../services/publicVisitService";
import { sanitizeAdminCustomerViewTelemetryString } from "../utils/adminCustomerViewTelemetry";
import { consumeShareVisitReferrer } from "../utils/shareVisitReferrer";

const RouteTracker: React.FC = () => {
  const location = useLocation();
  const { isLoading, isAuthenticated, user, hasRole } = useAuth();
  const lastPublicVisitPathRef = useRef<string | null>(null);

  useEffect(() => {
    // Wait for auth hydration before recording the initial visit.
    if (isLoading) return;
    const visitPath = location.pathname + location.search;
    if (lastPublicVisitPathRef.current === visitPath) return;
    // Auth updates, anchor scrolling and StrictMode effect replay are not new visits.
    lastPublicVisitPathRef.current = visitPath;
    const pageUrl = `${window.location.origin}${visitPath}`;
    void publicVisitService.recordVisit(
      pageUrl,
      consumeShareVisitReferrer(pageUrl, document.referrer),
    );
  }, [isLoading, location.pathname, location.search]);

  useEffect(() => {
    const pageTitle = document.title || "Plutus Expert Investing";
    const sanitizedPathname = sanitizeAdminCustomerViewTelemetryString(location.pathname);
    const sanitizedFullPath = sanitizeAdminCustomerViewTelemetryString(
      location.pathname + location.search + location.hash
    );
    const pageLocation = `${window.location.origin}${sanitizedFullPath}`;

    let userRole: string | undefined;
    if (isAuthenticated && user) {
      if (hasRole("ADMIN")) {
        userRole = "admin";
      } else if (hasRole("PROVIDER")) {
        userRole = "provider";
      } else {
        userRole = "investor";
      }
    }

    const pageContext = analyticsService.getPageContext(
      sanitizedPathname,
      isAuthenticated,
      userRole
    );

    analyticsService.trackPageView(sanitizedFullPath, pageTitle, pageContext, pageLocation);

    trackingService.page(sanitizedFullPath, pageTitle, {
      is_authenticated: isAuthenticated,
      user_role: userRole,
      page_type: pageContext.page_type,
      content_group: pageContext.content_group,
    });

    if (isAuthenticated) {
      intercomService.updateEngagementMetrics({
        lastPageViewed: sanitizedPathname,
      });
    }
  }, [location, isAuthenticated, user, hasRole]);

  return null;
};

export default RouteTracker;
